import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import {
  listTemplates, createTemplate, setTemplateDefault, archiveTemplate, templateUsage,
  type CmsTemplate, type CmsScope, type EntityKind, type TemplateUsage,
} from '../../lib/cmsAdmin';
import { listOrganizations, type Organization } from '../../lib/tenancy';
import { ApiError } from '../../lib/apiClient';
import { usePageChrome } from '../../admin/pageChrome';
import { useAdminScope } from '../../admin/scope';
import './AdminPages.css';
import './AdminCms.css';

/**
 * Templates: one layout, many entities.
 *
 * ── A TEMPLATE IS A LIVE REFERENCE, AND THE LIST SAYS SO ─────────────────────────────
 *
 * Events do not hold a copy of their layout. They resolve through a template every time
 * they are rendered, so publishing one changes every event that uses it, at once. The
 * count in the Used by column is the whole reason this list exists rather than being a
 * dropdown somewhere — before touching a template, somebody should be able to see what
 * it is attached to.
 */
export function AdminCmsTemplates() {
  const { organizationId } = useAdminScope();
  const [templates, setTemplates] = useState<CmsTemplate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [entityKind, setEntityKind] = useState<EntityKind | ''>('');
  const [reload, setReload] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);

  usePageChrome({
    title: 'Templates',
    subtitle: 'Layouts that many events share. Publishing one changes every event that '
      + 'resolves through it.',
  });

  useEffect(() => {
    setTemplates(null);
    listTemplates({ organizationId, entityKind: entityKind || null })
      .then(setTemplates)
      .catch((err: unknown) => setError(err instanceof ApiError
        ? err.message : 'The templates could not be loaded.'));
  }, [organizationId, entityKind, reload]);

  const shown = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return templates ?? [];
    return (templates ?? []).filter((t) => t.name.toLowerCase().includes(term)
      || (t.description ?? '').toLowerCase().includes(term));
  }, [templates, search]);

  async function toggleDefault(template: CmsTemplate) {
    const making = !template.isDefault;
    if (making && !window.confirm(
      `Make "${template.name}" the default for ${template.scope === 'platform'
        ? 'the whole platform' : 'this organization'}? Every event without a template of `
      + 'its own will start resolving through it.',
    )) return;
    setBusyId(template.id);
    try {
      await setTemplateDefault(template.id, making);
      setReload((n) => n + 1);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That could not be changed.');
    } finally { setBusyId(null); }
  }

  async function archive(template: CmsTemplate) {
    if (!window.confirm(
      `Archive "${template.name}"? Events using it will fall back to the next template in `
      + 'the chain. The server refuses this if it would leave events with nothing.',
    )) return;
    setBusyId(template.id);
    try {
      await archiveTemplate(template.id);
      setReload((n) => n + 1);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That template could not be archived.');
    } finally { setBusyId(null); }
  }

  return (
    <div className="adm-page">
      {error && <div className="alert alert--error" role="alert">{error}</div>}

      <div className="adm-filters">
        <input className="adm-select" type="search" placeholder="Search templates…"
          value={search} onChange={(e) => setSearch(e.target.value)}
          aria-label="Search templates" />
        <select className="adm-select" value={entityKind} aria-label="Filter by kind"
          onChange={(e) => setEntityKind(e.target.value as EntityKind | '')}>
          <option value="">All kinds</option>
          <option value="event">Event</option>
          <option value="venue">Venue</option>
          <option value="artist">Artist</option>
          <option value="standalone">Standalone</option>
        </select>
        <Link className="button button--primary" to="/admin/content/templates/new">New template</Link>
      </div>

      {!templates && !error && <p className="adm-lede">Loading templates…</p>}
      {templates && shown.length === 0 && (
        <p className="adm-lede">
          No templates {search || entityKind ? 'match that filter' : 'yet'}.
        </p>
      )}

      {shown.length > 0 && (
        <table className="adm-table">
          <thead>
            <tr>
              <th>Name</th><th>Kind</th><th>Scope</th><th>Live</th><th>Draft</th>
              <th>Used by</th><th>Created</th><th />
            </tr>
          </thead>
          <tbody>
            {shown.map((t) => (
              <tr key={t.id}>
                <td>
                  <Link to={`/admin/content/templates/${t.id}`}>{t.name}</Link>
                  {t.isDefault && <span className="badge badge--ok">Default</span>}
                  {t.description && <div className="adm-muted">{t.description}</div>}
                </td>
                <td>{t.entityKind}</td>
                <td>{t.scope === 'platform' ? 'Platform' : 'Organization'}</td>
                <td>{t.publishedVersion ? `v${t.publishedVersion}` : <span className="adm-muted">never</span>}</td>
                <td>{t.draftVersion ? <span className="badge badge--pending">v{t.draftVersion}</span> : '—'}</td>
                <td><UsedBy template={t} /></td>
                <td className="adm-nowrap">{new Date(t.createdAt).toLocaleDateString()}</td>
                <td className="adm-actions">
                  <button type="button" className="button button--ghost adm-btn-sm" disabled={busyId === t.id}
                    onClick={() => { void toggleDefault(t); }}>
                    {t.isDefault ? 'Unset default' : 'Make default'}
                  </button>
                  <button type="button" className="button button--ghost adm-btn-sm" disabled={busyId === t.id}
                    onClick={() => { void archive(t); }}>
                    Archive
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/**
 * How many events this template is responsible for.
 *
 * Fetched per row because the figure is a live count over events, not a column on the
 * template. A template list that reported a cached number would be reassuring and wrong.
 */
function UsedBy({ template }: { template: CmsTemplate }) {
  const [usage, setUsage] = useState<TemplateUsage | null>(null);

  useEffect(() => {
    let cancelled = false;
    templateUsage(template.id)
      .then((u) => { if (!cancelled) setUsage(u); })
      .catch(() => { /* The list is still useful without the count. */ });
    return () => { cancelled = true; };
  }, [template.id]);

  if (!usage) return <span className="adm-muted">…</span>;
  const total = usage.assigned + usage.viaDefault;
  if (total === 0) return <span className="adm-muted">no events</span>;
  return (
    <span title={`${usage.assigned} assigned directly, up to ${usage.viaDefault} through the default chain.`}>
      {usage.assigned > 0 && <>{usage.assigned} assigned</>}
      {usage.assigned > 0 && usage.viaDefault > 0 && ', '}
      {usage.viaDefault > 0 && <>up to {usage.viaDefault} by default</>}
    </span>
  );
}

/** Create a template. The document comes later, in the editor. */
export function AdminCmsTemplateNew() {
  const navigate = useNavigate();
  const { organizationId } = useAdminScope();

  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [scope, setScope] = useState<CmsScope>(organizationId ? 'organization' : 'platform');
  const [org, setOrg] = useState(organizationId ?? '');
  const [entityKind, setEntityKind] = useState<EntityKind>('event');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  usePageChrome({
    title: 'New template',
    breadcrumb: [
      { label: 'Content & design' },
      { label: 'Templates', to: '/admin/content/templates' },
      { label: 'New' },
    ],
  });

  useEffect(() => { listOrganizations().then(setOrgs).catch(() => setOrgs([])); }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const created = await createTemplate({
        scope,
        organizationId: scope === 'organization' ? (org || null) : null,
        entityKind,
        name: name.trim(),
        description: description.trim() || null,
      });
      navigate(`/admin/content/templates/${created.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The template could not be created.');
      setBusy(false);
    }
  }

  return (
    <div className="adm-page">
      <form className="adm-form cms-form" onSubmit={submit}>
        {error && <div className="alert alert--error" role="alert">{error}</div>}

        <label className="field">
          <span className="field__label">Name</span>
          <input className="field__input" value={name} required maxLength={120}
            placeholder="Default Event Page"
            onChange={(e) => setName(e.target.value)} />
        </label>

        <label className="field">
          <span className="field__label">Description</span>
          <input className="field__input" value={description} maxLength={240}
            placeholder="What this layout is for"
            onChange={(e) => setDescription(e.target.value)} />
        </label>

        <label className="field">
          <span className="field__label">Applies to</span>
          <select className="field__input" value={entityKind}
            onChange={(e) => setEntityKind(e.target.value as EntityKind)}>
            <option value="event">Events</option>
            <option value="venue">Venues</option>
            <option value="artist">Artists</option>
            <option value="standalone">Standalone pages</option>
          </select>
          <span className="field__help">
            Only event blocks exist today, so an event template is the one that renders
            anything.
          </span>
        </label>

        <label className="field">
          <span className="field__label">Owned by</span>
          <select className="field__input" value={scope}
            onChange={(e) => setScope(e.target.value as CmsScope)}>
            <option value="organization">An organization</option>
            <option value="platform">The platform</option>
          </select>
          <span className="field__help">
            A platform template renders for every organization, so it may only use
            platform-owned media.
          </span>
        </label>

        {scope === 'organization' && (
          <label className="field">
            <span className="field__label">Organization</span>
            <select className="field__input" value={org} required
              onChange={(e) => setOrg(e.target.value)}>
              <option value="">Choose one…</option>
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </label>
        )}

        <div className="adm-actions">
          <button type="submit" className="button button--primary" disabled={busy || !name.trim()}>
            {busy ? 'Creating…' : 'Create and edit'}
          </button>
          <Link className="button button--ghost" to="/admin/content/templates">Cancel</Link>
        </div>
      </form>
    </div>
  );
}
